import {
  type CurrentSportYearRecord,
  rdbV1DefaultYearList,
  rdbV1FiltersSportsList,
  type SlimSiteResponse,
  type SportFilter,
  v1SitesDetail,
  v1SitesOfferSetDetail,
  v1SitesSlimSitesList,
} from '@on3/api';
import { getFulfilledValue, proxyApi } from '@on3/ui-lib/utils/api';
import { getSiteMenus } from '@on3/ui-lib/utils/sites';
import type {
  GetServerSidePropsContext,
  NextComponentType,
  NextPageContext,
} from 'next';

import type { IGenericPageProps } from '../api/schema/custom-contracts';
import type { SiteServerProps } from '../contexts/SiteProvider';
import { filterRankingSports } from '../utils/filterRankingSports';

const withValidUrl = (
  WrappedComponent: NextComponentType<NextPageContext, unknown, any>,
) =>
  function WithValidUrl(props: IGenericPageProps) {
    const currentSite = props?.siteData?.currentSite;

    if (currentSite) {
      return <WrappedComponent {...props} />;
    }

    return null;
  };

const CURRENT_YEAR = new Date().getFullYear();
const NEXT_YEAR = CURRENT_YEAR + 1;

const DEFAULT_YEARS: CurrentSportYearRecord = {
  footballRecruitingYear: NEXT_YEAR,
  nonFootballRecruitingYear: NEXT_YEAR,
  footballSeasonYear: CURRENT_YEAR,
  nonFootballSeasonYear: CURRENT_YEAR,
  transferYear: NEXT_YEAR,
  nbaDraftYear: CURRENT_YEAR,
  nflDraftYear: CURRENT_YEAR,
  highSchoolYear: CURRENT_YEAR,
  nilYear: CURRENT_YEAR,
};

withValidUrl.isValid = async (
  ctx: GetServerSidePropsContext,
  siteKey?: number | null,
) => {
  try {
    const siteUrls = await v1SitesSlimSitesList(proxyApi, {});

    // Default to On3
    const defaultSiteKey = ctx.resolvedUrl?.startsWith('/rivals') ? 411 : 44;
    let slimSite = siteUrls.find(
      (site: SlimSiteResponse) => site.key === defaultSiteKey,
    );

    if (siteKey) {
      slimSite = siteUrls.find((url: SlimSiteResponse) => url.key === siteKey);
    } else {
      if (ctx?.query?.team) {
        slimSite = siteUrls?.find(
          (url: SlimSiteResponse) =>
            url.url?.toLowerCase() ===
            `/teams/${(ctx?.query?.team as string)?.toLowerCase()}`,
        );
      } else if (ctx?.query?.site) {
        slimSite = siteUrls?.find(
          (url: SlimSiteResponse) =>
            url.url?.toLowerCase() ===
            `/sites/${(ctx?.query?.site as string)?.toLowerCase()}`,
        );
      } else if (ctx?.query?.college) {
        slimSite = siteUrls?.find(
          (url: SlimSiteResponse) =>
            url.url?.toLowerCase() ===
            `/college/${(ctx?.query?.college as string).toLowerCase()}`,
        );
      }
    }

    if (!slimSite?.key || !slimSite?.live) {
      console.error({ url: ctx?.req?.url, error: 'Valid Url Check: line 62' });

      return null;
    }

    const [currentSiteResult, siblingSiteResult] = await Promise.allSettled([
      v1SitesDetail(proxyApi, { key: slimSite.key }),
      slimSite.siblingSiteKey
        ? v1SitesDetail(proxyApi, { key: slimSite.siblingSiteKey })
        : Promise.resolve(null),
    ]);

    const currentSite = getFulfilledValue(currentSiteResult);
    const siblingSite = getFulfilledValue(siblingSiteResult);

    if (!currentSite) {
      console.error({
        url: ctx?.req?.url,
        error: `Current site failed ${slimSite.key} `,
      });

      return null;
    }

    const siteMenus = getSiteMenus(currentSite, ctx);
    const offersSiteKey = siteKey
      ? siteKey
      : currentSite?.isChannel
        ? siblingSite?.key || 44
        : currentSite.key;

    const [
      primaryMenu,
      secondaryMenu,
      trendingMenu,
      sportsData,
      defaultYears,
      subscriptionDataReq,
    ] = await Promise.allSettled([
      siteMenus[0] ? siteMenus[0]() : null,
      siteMenus[1] ? siteMenus[1]() : null,
      siteMenus[2] ? siteMenus[2]() : null,
      rdbV1FiltersSportsList(proxyApi),
      rdbV1DefaultYearList(proxyApi),
      v1SitesOfferSetDetail(proxyApi, {
        siteKey: offersSiteKey,
      }),
    ]);

    const sportsDataResponse: SportFilter[] =
      getFulfilledValue(sportsData) || [];

    const rankingSports = filterRankingSports(sportsDataResponse) || [];

    const scheduleSports = filterRankingSports(sportsDataResponse) || [];

    const baseball = sportsDataResponse.find((s) => s?.key === 4);

    if (baseball) {
      scheduleSports?.push(baseball);
    }

    const years = getFulfilledValue(defaultYears);
    const newYears: CurrentSportYearRecord = { ...DEFAULT_YEARS, ...years };

    const data: SiteServerProps = {
      currentSite,
      siblingSite,
      siteUrls,
      menuData: getFulfilledValue(primaryMenu),
      secondaryMenuData: getFulfilledValue(secondaryMenu),
      trendingMenuData: getFulfilledValue(trendingMenu),
      sportsData: sportsDataResponse,
      rankingSports,
      scheduleSports,
      defaultYears: newYears,
      subscriptionData: getFulfilledValue(subscriptionDataReq),
    };

    return data;
  } catch {
    return null;
  }
};

export default withValidUrl;
export { withValidUrl };
